import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import type { DotControl, DotSource, DotStatus } from "./types";
import WhiteTooltip from "../../components/WhiteTooltip";

export type DotEditSavePayload = {
  control_id: string;
  status: DotStatus;
  source: DotSource;
  comments: string;
  override?: boolean;
  override_reason?: string;
  reset_to_auto?: boolean;
};

interface DoTEditDrawerProps {
  open: boolean;
  control: DotControl | null;
  onClose: () => void;
  onSave: (payload: DotEditSavePayload) => Promise<void> | void;
  saving?: boolean;
  /** Phase 1: only Admins can save / reset */
  canEdit?: boolean;
  /** When set (e.g. opened from PID drilldown), show Back */
  onBack?: () => void;
  backLabel?: string;
}

const COMMENTS_MAX = 500;
const ACCENT = "#4F46E5";

const DoTEditDrawer: React.FC<DoTEditDrawerProps> = ({
  open,
  control,
  onClose,
  onSave,
  saving = false,
  canEdit = true,
  onBack,
  backLabel = "Back",
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [status, setStatus] = useState<DotStatus>(null);
  const [source, setSource] = useState<DotSource>(null);
  const [comments, setComments] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  useEffect(() => {
    if (!control) return;
    setStatus(control.status);
    setSource(control.source);
    setComments(control.comments || "");
    setOverrideReason(control.override_reason || "");
  }, [control]);

  if (!control) return null;

  const isPid = control.pid_mapped;
  const differsFromAuto =
    isPid && status != null && status !== control.auto_status;
  const needsReason = isPid && differsFromAuto;
  const statusUnset = status == null;

  const sectionCrumb = control.section
    ? `${control.section}${
        control.section_heading ? ` ${control.section_heading}` : ""
      }`
    : null;
  const subsectionCrumb = control.subsection
    ? `${control.subsection}${
        control.subsection_heading ? ` ${control.subsection_heading}` : ""
      }`
    : null;

  const guidelineText =
    (control.description || "").trim() ||
    (control.title || "").trim() ||
    "";
  const requirementText = (control.note || "").trim();

  const surface = isDark ? "#191C24" : "#FFFFFF";
  const muted = isDark ? "rgba(255,255,255,0.55)" : "#6B7280";
  const border = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
  const guidelineBg = isDark ? "rgba(59,130,246,0.12)" : "#EFF6FF";
  const guidelineBorder = isDark ? "rgba(59,130,246,0.28)" : "#BFDBFE";
  const requirementBg = isDark ? "rgba(99,102,241,0.14)" : "#EEF2FF";
  const requirementBorder = isDark ? "rgba(99,102,241,0.3)" : "#C7D2FE";
  const handleSave = async () => {
    if (!canEdit) return;
    if (needsReason && !(overrideReason || "").trim()) return;
    await onSave({
      control_id: control.id,
      status,
      source: isPid ? "SPARK" : source,
      comments,
      override: isPid ? differsFromAuto || control.override : false,
      override_reason: isPid && differsFromAuto ? overrideReason.trim() : "",
    });
  };

  const handleResetAuto = async () => {
    if (!canEdit) return;
    await onSave({
      control_id: control.id,
      status: control.auto_status,
      source: "SPARK",
      comments,
      reset_to_auto: true,
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", md: "55%" },
          bgcolor: surface,
          display: "flex",
          flexDirection: "column",
          p: 0,
          boxShadow: isDark
            ? "-8px 0 32px rgba(0,0,0,0.45)"
            : "-8px 0 32px rgba(15,23,42,0.12)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          px: 3,
          pt: 2,
          pb: 2,
          flexShrink: 0,
          borderBottom: `1px solid ${border}`,
        }}
      >
        {onBack ? (
          <Button
            variant="outlined"
            size="medium"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={onBack}
            disabled={saving}
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              borderColor: isDark ? "rgba(99,102,241,0.5)" : "#C7D2FE",
              color: ACCENT,
              bgcolor: isDark ? "rgba(79,70,229,0.12)" : "#EEF2FF",
              px: 1.5,
              "&:hover": {
                borderColor: ACCENT,
                bgcolor: isDark ? "rgba(79,70,229,0.22)" : "#E0E7FF",
              },
            }}
          >
            {backLabel}
          </Button>
        ) : null}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              bgcolor: isDark ? "rgba(79,70,229,0.22)" : "#EEF2FF",
              color: ACCENT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 700,
                lineHeight: 1.35,
                color: isDark ? "#fff" : "#111827",
              }}
            >
              {control.id} {control.title || control.description}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                fontSize: 13,
                color: muted,
                lineHeight: 1.4,
              }}
            >
              {[
                "DoT / MBSS",
                sectionCrumb,
                subsectionCrumb,
              ]
                .filter(Boolean)
                .join(" › ")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ flexShrink: 0, color: muted, mt: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          px: 3,
          pb: 1,
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Guideline */}
        {guidelineText ? (
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.75,
              mb: 1.5,
              borderRadius: 2,
              bgcolor: guidelineBg,
              border: `1px solid ${guidelineBorder}`,
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 20,
                color: isDark ? "#93C5FD" : "#2563EB",
                mt: 0.15,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: isDark ? "#93C5FD" : "#1D4ED8",
                  mb: 0.5,
                }}
              >
                Guideline
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: isDark ? "rgba(255,255,255,0.85)" : "#1F2937",
                  whiteSpace: "pre-wrap",
                }}
              >
                {guidelineText}
              </Typography>
            </Box>
          </Box>
        ) : null}

        {/* Requirement */}
        {requirementText ? (
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.75,
              mb: 2.5,
              borderRadius: 2,
              bgcolor: requirementBg,
              border: `1px solid ${requirementBorder}`,
            }}
          >
            <FormatQuoteRoundedIcon
              sx={{
                fontSize: 22,
                color: isDark ? "#A5B4FC" : "#4F46E5",
                mt: 0.1,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: isDark ? "#A5B4FC" : "#4338CA",
                  mb: 0.5,
                }}
              >
                Requirement
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.55,
                  fontStyle: "italic",
                  color: isDark ? "rgba(255,255,255,0.85)" : "#312E81",
                  whiteSpace: "pre-wrap",
                }}
              >
                {requirementText}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 1 }} />
        )}

        {/* Compliance Details */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <EditNoteRoundedIcon sx={{ fontSize: 20, color: ACCENT }} />
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: isDark ? "#fff" : "#111827",
            }}
          >
            Compliance Details
          </Typography>
        </Box>

        {!canEdit ? (
          <Typography sx={{ fontSize: 13, color: muted, mb: 2 }}>
            Only Admins can update this control.
          </Typography>
        ) : null}

        {/* Status */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: isDark ? "rgba(255,255,255,0.9)" : "#374151",
            }}
          >
            Compliance Status{" "}
            <Box component="span" sx={{ color: "#DC2626" }}>
              *
            </Box>
          </Typography>
          <WhiteTooltip
            title={
              isPid
                ? `Auto from linked PIDs: ${control.auto_status || "—"}. Override below if needed.`
                : statusUnset
                  ? "Status is not set yet. Fill compliance status, source, and comments here, then Save."
                  : "Update compliance status, source, and comments, then Save."
            }
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                color: statusUnset && !isPid
                  ? isDark
                    ? "#FCA5A5"
                    : "#DC2626"
                  : muted,
                cursor: "help",
              }}
            />
          </WhiteTooltip>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <RadioGroup
            row
            value={status ?? ""}
            onChange={(_, v) => {
              if (!canEdit) return;
              setStatus((v || null) as DotStatus);
            }}
            sx={{
              "& .MuiFormControlLabel-root": { mr: 2 },
              "& .MuiRadio-root.Mui-checked": { color: ACCENT },
            }}
          >
            <FormControlLabel
              value="Yes"
              control={<Radio size="small" disabled={!canEdit} />}
              label="Yes"
            />
            <FormControlLabel
              value="No"
              control={<Radio size="small" disabled={!canEdit} />}
              label="No"
            />
            {!isPid ? (
              <FormControlLabel
                value=""
                control={<Radio size="small" disabled={!canEdit} />}
                label="Unset"
              />
            ) : null}
          </RadioGroup>
        </Box>

        {isPid && differsFromAuto ? (
          <TextField
            label="Override reason"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            size="small"
            disabled={!canEdit}
            sx={{ mb: 2.5 }}
            error={needsReason && !(overrideReason || "").trim()}
            helperText="Required when status differs from auto calculation"
          />
        ) : null}

        {/* Source */}
        {!isPid ? (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                mb: 1,
                color: isDark ? "rgba(255,255,255,0.9)" : "#374151",
              }}
            >
              Source
            </Typography>
            <FormControl fullWidth size="small" disabled={!canEdit}>
              <Select
                displayEmpty
                value={source ?? ""}
                onChange={(e) =>
                  setSource((e.target.value || null) as DotSource)
                }
                sx={{
                  borderRadius: 2,
                  bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#F9FAFB",
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: muted, fontSize: 14 }}>
                        Select source
                      </Typography>
                    );
                  }
                  return selected as string;
                }}
              >
                <MenuItem value="">
                  <em>Unset</em>
                </MenuItem>
                <MenuItem value="SPARK">SPARK</MenuItem>
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Third party">Third party</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ) : null}

        {/* Comments */}
        <Box sx={{ mb: 1 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              mb: 1,
              color: isDark ? "rgba(255,255,255,0.9)" : "#374151",
            }}
          >
            Comments / Artefact notes
          </Typography>
          <TextField
            value={comments}
            onChange={(e) =>
              setComments(e.target.value.slice(0, COMMENTS_MAX))
            }
            fullWidth
            multiline
            minRows={3}
            size="small"
            disabled={!canEdit}
            placeholder="Add comments or notes (optional)"
            helperText={`${comments.length} / ${COMMENTS_MAX}`}
            FormHelperTextProps={{
              sx: {
                textAlign: "right",
                mx: 0,
                mt: 0.75,
                color: muted,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#F9FAFB",
              },
            }}
          />
        </Box>

        {control.updated_at ? (
          <Typography
            fontSize={12}
            sx={{ mt: 1.5, mb: 1, color: muted }}
          >
            Last updated {new Date(control.updated_at).toLocaleString()}
            {control.updated_by ? ` by ${control.updated_by}` : ""}
          </Typography>
        ) : null}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${border}`,
          display: "flex",
          gap: 1.5,
          justifyContent: "flex-end",
          alignItems: "center",
          flexWrap: "wrap",
          flexShrink: 0,
          bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#FAFAFA",
        }}
      >
        {onBack ? (
          <Button
            onClick={onBack}
            disabled={saving}
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              mr: "auto",
              color: ACCENT,
            }}
          >
            {backLabel}
          </Button>
        ) : canEdit && isPid && (control.override || differsFromAuto) ? (
          <Button
            onClick={handleResetAuto}
            disabled={saving}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: 600, mr: "auto" }}
          >
            Reset to auto
          </Button>
        ) : (
          <Box sx={{ mr: "auto" }} />
        )}
        {canEdit && onBack && isPid && (control.override || differsFromAuto) ? (
          <Button
            onClick={handleResetAuto}
            disabled={saving}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Reset to auto
          </Button>
        ) : null}
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 2.5,
            borderColor: border,
            color: isDark ? "#E5E7EB" : "#374151",
            bgcolor: surface,
          }}
        >
          {canEdit ? "Cancel" : "Close"}
        </Button>
        {canEdit ? (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || (needsReason && !(overrideReason || "").trim())}
            startIcon={<SaveOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 2.75,
              bgcolor: ACCENT,
              boxShadow: "none",
              "&:hover": { bgcolor: "#4338CA", boxShadow: "none" },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        ) : null}
      </Box>
    </Drawer>
  );
};

export default DoTEditDrawer;
